import React, { useState } from 'react';
import { Send, Loader2, RefreshCw } from 'lucide-react';

// Recent origin-change webhook deliveries + status, straight in the portal.
// GET /api/mgmt/v1/webhooks/deliveries?event_type=embed.origins.update
// Replay via POST .../deliveries/{id}/replay. Key or session auth.
const STATUS_COLOR = { success: 'text-emerald-300', failed: 'text-rose-300', pending: 'text-amber-300' };

export default function WebhookDeliveriesPanel({ apiBase, bearerKey }) {
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const authOpts = () => {
    const key = bearerKey?.trim();
    return { headers: key ? { Authorization: `Bearer ${key}` } : {}, credentials: key ? 'same-origin' : 'include' };
  };

  const load = async () => {
    setLoading(true); setErr(''); setRows(null);
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/webhooks/deliveries?event_type=embed.origins.update&limit=50`, authOpts());
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setRows(data.data || []);
    } catch (e) { setErr(String(e.message || e)); } finally { setLoading(false); }
  };

  const replay = async (id) => {
    try {
      const o = authOpts();
      await fetch(`${apiBase}/api/mgmt/v1/webhooks/deliveries/${id}/replay`, {
        method: 'POST', headers: o.headers, credentials: o.credentials,
      });
      load();
    } catch { /* surfaced on reload */ }
  };

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 mb-6" data-testid="webhook-deliveries-panel">
      <button
        type="button"
        onClick={() => { const n = !expanded; setExpanded(n); if (n && rows === null) load(); }}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="webhook-deliveries-toggle"
      >
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Origin-change webhook deliveries</span>
          <span className="text-[10px] text-muted-foreground">recent embed.origins.update sends</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/40 pt-3">
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-border/40 bg-background/40 text-muted-foreground hover:text-foreground"
            data-testid="webhook-deliveries-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          {err && <p className="text-[11px] text-rose-400" data-testid="webhook-deliveries-error">{err}</p>}

          {rows !== null && !err && (
            rows.length === 0 ? (
              <p className="text-[11px] text-muted-foreground" data-testid="webhook-deliveries-empty">
                No origin-change deliveries yet. Register a webhook endpoint subscribed to <code>embed.origins.update</code>, then change a workspace&rsquo;s origins.
              </p>
            ) : (
              <div className="rounded border border-border/40 divide-y divide-border/30" data-testid="webhook-deliveries-list">
                {rows.map((r) => (
                  <div key={r.delivery_id} className="px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid="webhook-delivery-row">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] font-semibold ${STATUS_COLOR[r.status] || 'text-muted-foreground'}`} data-testid="webhook-delivery-status">{r.status}</span>
                        {r.last_status_code ? <span className="text-[10px] text-muted-foreground">HTTP {r.last_status_code}</span> : null}
                        <span className="text-[10px] text-muted-foreground">· {r.attempts || 0} attempt(s)</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">{r.url}</div>
                      <div className="text-[10px] text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</div>
                    </div>
                    {r.status !== 'success' && (
                      <button
                        type="button"
                        onClick={() => replay(r.delivery_id)}
                        className="shrink-0 text-[10px] px-2 py-1 rounded border border-primary/50 bg-primary/10 text-primary"
                        data-testid="webhook-delivery-replay"
                      >
                        Replay
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
